import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useAuth0 } from "auth/react-auth0-spa";
import type { ChildrenProps } from "helpers";
import Log from "helpers/Log";
import React from "react";
import { useAppStore } from "src/state/appStore";
import type { DeviceStateChangeEvent } from "./types";

const { VITE_SIGNAL_R_URL } = window.__ENV;

export interface SignalRContextProps {
	onDeviceStateChange: (
		deviceId: string,
		onStateChange: (value: DeviceStateChangeEvent) => void
	) => () => Promise<void>;
}

export const SignalRContext = React.createContext<SignalRContextProps | null>(null);

const SignalR: React.FC<ChildrenProps> = props => {
	const tenantId = useAppStore(s => s.tenantId);
	const { getTokenSilently } = useAuth0();
	const url = `https://${VITE_SIGNAL_R_URL}/signalr/`;

	/**
	 * Binds to Device notification events.
	 * @param deviceId device id
	 * @param onStateChange callback for device state change event.
	 * @returns function to dispose of the connection.
	 */
	const onDeviceStateChange = (deviceId: string, onStateChange: (value: DeviceStateChangeEvent) => void) => {
		const connection = new HubConnectionBuilder()
			.configureLogging(LogLevel.Warning)
			.withUrl(`${url}devicenotifications?tenantId=${tenantId}&deviceId=${deviceId}`, {
				accessTokenFactory: () => getTokenSilently()
			})
			.build();

		connection
			.start()
			.then(() => {
				connection.on("DeviceStateChanged", onStateChange);
			})
			.catch(err => Log.error(err));

		return () => connection.stop();
	};

	return (
		<SignalRContext.Provider
			value={{
				onDeviceStateChange
			}}>
			{props.children}
		</SignalRContext.Provider>
	);
};

export default SignalR;